import { FirmFolderTree } from "@/components/features/settings/firm/FirmFolderTree";
import { LoadingSpinner, Typography } from "@/components/ui";
import { retrieveFirmFilesOptions } from "@/lib/data/react-query/firm/firm-file";
import { buildFirmFileBreadcrumbs } from "@/lib/utils/firmFile";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

interface FirmDestinationFolderPickerProps {
  selectedFolderId: string;
  setSelectedFolderId: (folderId: string) => void;
}

export function FirmDestinationFolderPicker({
  selectedFolderId,
  setSelectedFolderId,
}: FirmDestinationFolderPickerProps) {
  const { data, isLoading } = useQuery(retrieveFirmFilesOptions());

  const files = useMemo(() => data?.data ?? [], [data?.data]);

  const selectionDescription = useMemo(() => {
    if (!selectedFolderId) {
      return "Firm Files (root)";
    }

    const crumbs = buildFirmFileBreadcrumbs(files, selectedFolderId);
    if (crumbs.length === 0) {
      return "Firm Files (root)";
    }

    return ["Firm Files", ...crumbs.map((c) => c.name)].join(" / ");
  }, [files, selectedFolderId]);

  if (isLoading) {
    return (
      <div className="border-border/80 bg-card flex h-full min-h-0 flex-col items-center justify-center border-l">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="border-border/80 bg-card flex h-full min-h-0 flex-col border-l">
      <div className="p-4 pb-3">
        <Typography className="text-xl font-semibold">
          Destination folder
        </Typography>
        <Typography className="text-muted-foreground mt-1 text-xs">
          Choose where to save the signed document in your firm library.
        </Typography>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 px-3.5 pb-3.5">
        <div className="bg-muted/20 min-w-0 rounded-xl border px-4 py-3">
          <Typography className="text-muted-foreground text-xs font-medium">
            Selected destination
          </Typography>
          <Typography className="mt-1 max-w-full min-w-0 truncate text-sm font-medium break-normal whitespace-nowrap">
            {selectionDescription}
          </Typography>
        </div>

        <div className="min-h-0 flex-1 overflow-auto rounded-xl border p-2">
          <FirmFolderTree
            files={files}
            currentFolderId={selectedFolderId}
            onNavigateToFolder={setSelectedFolderId}
          />
        </div>
      </div>
    </div>
  );
}
